import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { isIgnoredFile } from "@/domains/documents/constants/fileTypes";
import type { PickerDocument } from "@/components/documents/DocumentPicker";
import * as pmRepo from "@/stores/pm/repository";
import type { LinkedDocument } from "@/domains/pm/types";
import type { ProtocolAttachment } from "@/domains/pm/utils/protocolAttachments";

let localCounter = 0;

/**
 * The files the author attaches on step 3, owned by the wizard so their object
 * URLs live exactly as long as it does: revoked on removal and on close, never
 * while a tile might still be reading one.
 */
export function useProtocolAttachments() {
  const [added, setAdded] = useState<ProtocolAttachment[]>([]);
  const [resolving, setResolving] = useState(false);
  // Every URL minted and not yet revoked, for the unmount cleanup — state alone
  // would hand the cleanup a stale list.
  const urlsRef = useRef(new Set<string>());

  useEffect(() => {
    const urls = urlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const addFiles = useCallback((files: File[]) => {
    const kept = files.filter((file) => !isIgnoredFile(file));
    if (kept.length === 0) return;
    const next = kept.map((file): ProtocolAttachment => {
      localCounter += 1;
      const url = URL.createObjectURL(file);
      urlsRef.current.add(url);
      return { key: `local:${localCounter}`, origin: "local", file, url };
    });
    setAdded((current) => [...current, ...next]);
  }, []);

  /** The picker hands back ids and names; a tile needs the storage path. */
  const addDocuments = useCallback(async (picked: PickerDocument[], failedMessage: string) => {
    if (picked.length === 0) return;
    setResolving(true);
    try {
      const rows = await pmRepo.getLinkedDocumentsByIds(picked.map((doc) => Number(doc.id)));
      setAdded((current) => {
        const present = new Set(current.map((attachment) => attachment.key));
        const fresh = rows
          .map((doc): ProtocolAttachment => ({ key: `doc:${doc.id}`, origin: "stored", doc }))
          .filter((attachment) => !present.has(attachment.key));
        return [...current, ...fresh];
      });
    } catch (error) {
      console.error("Protocol attachment lookup failed", error);
      toast.error(failedMessage);
    } finally {
      setResolving(false);
    }
  }, []);

  // Read by `remove`, which revokes outside the state updater: StrictMode runs
  // updaters twice, and a side effect has no business in one.
  const addedRef = useRef(added);
  useEffect(() => {
    addedRef.current = added;
  }, [added]);

  const remove = useCallback((key: string) => {
    const target = addedRef.current.find((attachment) => attachment.key === key);
    if (target?.origin === "local") {
      URL.revokeObjectURL(target.url);
      urlsRef.current.delete(target.url);
    }
    setAdded((current) => current.filter((attachment) => attachment.key !== key));
  }, []);

  return { added, resolving, addFiles, addDocuments, remove };
}

export type ProtocolIssueAttachmentsStatus = "idle" | "loading" | "ready" | "error";

const NO_ATTACHMENTS: ReadonlyMap<number, LinkedDocument[]> = new Map();

/**
 * The attachments of the period's issues, read only once the author asks for
 * them. Fetched for the whole loaded period rather than the ticked issues, so
 * ticking and unticking on step 2 never refetches; the plan narrows to the
 * ticked ones.
 */
export function useProtocolIssueAttachments(issueIds: readonly number[], enabled: boolean) {
  const signature = [...issueIds].sort((a, b) => a - b).join(",");
  const query = useQuery({
    queryKey: ["protocol-issue-attachments", signature],
    queryFn: ({ signal }) => pmRepo.getProtocolIssueDocuments([...issueIds], signal),
    enabled: enabled && issueIds.length > 0,
    // Fresh for one sitting in the wizard; a reopen reads again.
    staleTime: 60_000,
  });

  let status: ProtocolIssueAttachmentsStatus = "idle";
  if (enabled) {
    if (issueIds.length === 0) status = "ready";
    else if (query.isError) status = "error";
    else if (query.data) status = "ready";
    else status = "loading";
  }
  return {
    status,
    byIssue: query.data ?? NO_ATTACHMENTS,
    retry: () => void query.refetch(),
  };
}
